import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import merge from 'lodash/merge';

import {
  selectBMIMeasurementEntity,
  selectBMIMeasurementModalEntity,
  selectWeightMeasurementEntity,
  selectWeightMeasurementModalEntity,
} from '../../../redux/profiles/patientHealthboard/selectors';
import {
  checkForAvailableSourceData,
  getSourceDataWithType,
  adaptResponse,
  createTooltips,
  makeValueForGraf,
  calculateAverageValue,
  getOriginalProviderCode,
} from '../utils';
import {
  CHART_FILTERS_TYPE,
  DATA_SOURCE_TYPE,
  VITALS_ACTIVITY_TYPE,
} from '../constants';
import { singlePointValuesModalActions } from '../../../redux/profiles/patientHealthboard/single-point-values-modal';
import { useSafeDataSourceTitle } from '../../common/Charts/hooks';

const { BMI, WEIGHT } = VITALS_ACTIVITY_TYPE;
const { CCD, DEVICES, ALL_SOURCES } = DATA_SOURCE_TYPE;
const { QUARTER, YEAR } = CHART_FILTERS_TYPE;

export function useCCDSourceWeightBMI({
  rowData,
  activeFilter,
  singleDayMode,
  options,
  dataType,
  sourceType,
  currentChartInstance,
  formatMessage,
  mainUser,
  targetUserFirstName,
  isWeightInKg,
}) {
  const bmiDataEntity = adaptResponse(useSelector(selectBMIMeasurementEntity));
  const bmiDataModal = adaptResponse(
    useSelector(selectBMIMeasurementModalEntity),
  );
  const weightDataEntity = adaptResponse(
    useSelector(selectWeightMeasurementEntity),
  );
  const weightDataModal = adaptResponse(
    useSelector(selectWeightMeasurementModalEntity),
  );

  const isApplicableModalOneValues = [QUARTER.type, YEAR.type].includes(
    activeFilter,
  );

  const { getSafeDataSourceTitle } = useSafeDataSourceTitle();

  const ccdSourceTooltipLabel = getSafeDataSourceTitle(
    CCD,
    formatMessage,
    mainUser,
    targetUserFirstName,
  );

  const deviceSourceTooltipLabel = getSafeDataSourceTitle(
    DEVICES,
    formatMessage,
    mainUser,
    targetUserFirstName,
  );

  const getSafeData = useMemo(() => {
    switch (dataType) {
      case BMI:
        return singleDayMode ? bmiDataModal : bmiDataEntity;
      case WEIGHT:
        return singleDayMode ? weightDataModal : weightDataEntity;
    }
  }, [
    singleDayMode,
    dataType,
    bmiDataEntity,
    bmiDataModal,
    weightDataEntity,
    weightDataModal,
  ]);

  if (![BMI, WEIGHT].includes(dataType)) return {};

  const safeDataDevices = getSourceDataWithType(getSafeData, DEVICES);
  const safeDataCCD = getSourceDataWithType(getSafeData, CCD);

  const dataEntityDevices = checkForAvailableSourceData(safeDataDevices)
    ? makeValueForGraf(
        safeDataDevices.measurements,
        'value',
        activeFilter,
        false,
        dataType,
        singleDayMode,
        false,
      )
    : [];

  const dataEntityCCD = checkForAvailableSourceData(safeDataCCD)
    ? makeValueForGraf(
        safeDataCCD.measurements,
        'value',
        activeFilter,
        false,
        dataType,
        singleDayMode,
        false,
        true,
      )
    : [];

  const rowDataDevicesForAverageCalc = checkForAvailableSourceData(
    safeDataDevices,
  )
    ? safeDataDevices.measurements.map(item => item.value)
    : [];
  const rowDataCCDForAverageCalc = checkForAvailableSourceData(safeDataCCD)
    ? safeDataCCD.measurements.map(item => item.value)
    : [];

  const averageValueDevices = calculateAverageValue(
    rowDataDevicesForAverageCalc,
  );
  const averageValueCCD = calculateAverageValue(rowDataCCDForAverageCalc);

  const timeSets = checkForAvailableSourceData(safeDataCCD)
    ? makeValueForGraf(
        safeDataCCD.measurements,
        'value',
        activeFilter,
        true,
        dataType,
        singleDayMode,
        false,
        true,
      )
    : [];

  const dispatch = useDispatch();

  const handleShowAverageValue = () => {
    if (
      checkForAvailableSourceData(safeDataDevices) &&
      checkForAvailableSourceData(safeDataCCD) &&
      sourceType === ALL_SOURCES
    ) {
      return calculateAverageValue([
        ...rowDataDevicesForAverageCalc,
        ...rowDataCCDForAverageCalc,
      ]);
    }
    if (
      !checkForAvailableSourceData(safeDataDevices) &&
      checkForAvailableSourceData(safeDataCCD) &&
      sourceType === ALL_SOURCES
    ) {
      return averageValueCCD;
    }
    if (
      checkForAvailableSourceData(safeDataDevices) &&
      !checkForAvailableSourceData(safeDataCCD) &&
      sourceType === ALL_SOURCES
    ) {
      return averageValueDevices;
    }
    if (sourceType === DEVICES) {
      return averageValueDevices;
    }
    if (sourceType === CCD) {
      return averageValueCCD;
    }
    return '';
  };

  const datasets = [
    {
      ...rowData.datasets[0],
      label: 'dataDevices',
      data: [DEVICES, ALL_SOURCES].includes(sourceType)
        ? dataEntityDevices
        : [],
      sourceType: {
        title: deviceSourceTooltipLabel,
        code: DATA_SOURCE_TYPE.DEVICES,
        codes:
          safeDataDevices && safeDataDevices.measurements
            ? safeDataDevices.measurements.map(el => {
                return {
                  providerCode: el.originalProviderCodes,
                  value: el.value,
                  measuredAt: el.measuredAt,
                };
              })
            : null,
      },
      order: 2,
    },
    {
      ...rowData.datasets[0],
      label: 'dataCCD',
      type: isApplicableModalOneValues ? 'line' : 'bubble',
      data: [CCD, ALL_SOURCES].includes(sourceType) ? dataEntityCCD : [],
      backgroundColor: '#f87f5c',
      borderColor: '#f87f5c',
      borderWidth: 0,
      ...(isApplicableModalOneValues && {
        fill: false,
        showLine: true,
        borderWidth: 1,
        pointBackgroundColor: '#f87f5c',
        pointBorderColor: '#f87f5c',
        pointBorderWidth: 1,
        pointHoverBackgroundColor: '#f87f5c',
        pointHoverBorderColor: '#f87f5c',
        pointHoverBorderWidth: 1,
      }),
      pointStyle: 'rectRot',
      radius: 3,
      sourceType: {
        title: ccdSourceTooltipLabel,
        code: DATA_SOURCE_TYPE.CCD,
      },
      order: 1,
    },
  ];

  const bmiWeightData = {
    ...rowData,
    ...(isApplicableModalOneValues &&
      checkForAvailableSourceData(safeDataCCD) && { timeSets }),
    datasets,
    averageOrTotal: {
      ...rowData.averageOrTotal,
      showAverageLine:
        checkForAvailableSourceData(safeDataDevices) &&
        checkForAvailableSourceData(safeDataCCD) &&
        sourceType === ALL_SOURCES,
      value: handleShowAverageValue(),
    },
    availableDataSource: {
      ccd: checkForAvailableSourceData(safeDataCCD),
      devices: checkForAvailableSourceData(safeDataDevices),
    },
    optionsToolTip: createTooltips(datasets),
  };

  useEffect(() => {
    if (currentChartInstance && currentChartInstance.chartInstance) {
      currentChartInstance.chartInstance.update();
    }
  }, [currentChartInstance, sourceType]);

  const bmiWeightOptions = merge(
    { ...options },
    {
      hover: {
        mode: 'nearest',
      },
      onClick: (evt, element) => {
        if (element.length > 0) {
          if (currentChartInstance && currentChartInstance.current) {
            const allEntity = currentChartInstance.current.chartInstance.data;
            const indexActiveDataset =
              element[0]._chart.tooltip._active[0]._datasetIndex;
            const isHealthRecordSource =
              allEntity.datasets[indexActiveDataset].sourceType.code === CCD;
            if (isApplicableModalOneValues && isHealthRecordSource) {
              dispatch(
                singlePointValuesModalActions.open({
                  metricSettings: { isWeightInKg },
                  type: dataType,
                  value:
                    allEntity.datasets[indexActiveDataset].data[
                      element[0]._index
                    ],
                  monthTitle: allEntity.timeSets[element[0]._index],
                  entity:
                    safeDataCCD && safeDataCCD.measurements
                      ? safeDataCCD.measurements
                      : [],
                }),
              );
            }
            allEntity.optionsToolTip[indexActiveDataset][0].forEach(
              (item, index) => {
                if (index === element[0]._index && item.keepTooltipOpen) {
                  // eslint-disable-next-line no-param-reassign
                  item.keepTooltipOpen = false;
                  // eslint-disable-next-line no-param-reassign
                } else item.keepTooltipOpen = index === element[0]._index;
              },
            );
          }
        }
      },
    },
  );

  return { bmiWeightData, bmiWeightOptions };
}



// WEBPACK FOOTER //
// ./src/components/PatientHealthboard/Chart/useCCDSourceWeightBMI.jsx